import {
  Briefcase,
  Calendar,
  Copy,
  GraduationCap,
  Heart,
  Languages,
  Mail,
  MapPin,
  Star,
  User,
} from "lucide-react";
import { useState } from "react";
import type { Candidate } from "../data/mock-candidates";

type CandidateProfileProps = {
  candidate: Candidate;
  onScoutClick?: () => void;
};

function MemberBadge({ type }: { type: "HIGHCLASS" | "TALENT" }) {
  if (type === "HIGHCLASS") {
    return (
      <span className="rounded-sm bg-amber-900 px-1.5 py-0.5 font-bold text-[11px] text-amber-100 tracking-wider">
        HIGHCLASS
      </span>
    );
  }
  return (
    <span className="rounded-sm bg-teal-600 px-1.5 py-0.5 font-bold text-[11px] text-white tracking-wider">
      TALENT
    </span>
  );
}

function HotBadge() {
  return (
    <span className="rounded-sm bg-red-500 px-1.5 py-0.5 font-bold text-[11px] text-white tracking-wider">
      HOT
    </span>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2 border-gray-200 border-b pb-2">
      <h3 className="font-bold text-sm text-gray-800">{children}</h3>
    </div>
  );
}

type TabId = "resume" | "history" | "message";

export function CandidateProfile({ candidate, onScoutClick }: CandidateProfileProps) {
  const [activeTab, setActiveTab] = useState<TabId>("resume");

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: "resume", label: "職務経歴書" },
    { id: "history", label: "この候補者との関わり", count: 0 },
    { id: "message", label: "メッセージ", count: 0 },
  ];

  const headerColor =
    candidate.memberType === "HIGHCLASS"
      ? "bg-amber-50 border-amber-200"
      : "bg-teal-50 border-teal-200";

  return (
    <div>
      {/* プロフィールヘッダー */}
      <div className={`rounded-t-lg border p-5 ${headerColor}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            {/* アバター */}
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
              <User className="h-7 w-7 text-gray-400" />
            </div>

            <div>
              <div className="flex items-center gap-2">
                <MemberBadge type={candidate.memberType} />
                {candidate.isHot ? <HotBadge /> : null}
              </div>

              <div className="mt-2 flex items-center gap-3 text-gray-800">
                {candidate.age ? (
                  <span className="font-bold text-lg">{candidate.age}歳</span>
                ) : candidate.estimatedAge ? (
                  <span
                    className="font-bold text-green-600 text-lg"
                    title="スカウトメールAIによる分析の推定年齢です"
                  >
                    推定{candidate.estimatedAge}歳
                  </span>
                ) : null}
                <span className="text-sm">{candidate.gender}</span>
                <span className="flex items-center gap-0.5 text-sm">
                  <MapPin className="h-3.5 w-3.5 text-gray-500" />
                  {candidate.prefecture}
                </span>
              </div>

              <div className="mt-1 text-sm text-gray-600">
                {candidate.employmentStatus === "現職" ? (
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3.5 w-3.5 text-gray-400" />
                    {candidate.currentCompany} / {candidate.currentDepartment} /{" "}
                    {candidate.currentPosition}
                  </span>
                ) : (
                  <span className="text-orange-600">離職中（即日入社可能）</span>
                )}
              </div>
            </div>
          </div>

          {/* アクションボタン */}
          <div className="flex items-center gap-2">
            <button
              className="rounded bg-purple-600 px-5 py-2 font-medium text-sm text-white shadow-sm hover:bg-purple-700"
              onClick={onScoutClick}
              type="button"
            >
              <Mail className="mr-1.5 inline h-3.5 w-3.5" />
              スカウトを送る
            </button>
            <button
              className="rounded border border-gray-300 bg-white p-2 text-gray-400 hover:bg-gray-50 hover:text-purple-500"
              title="気になる"
              type="button"
            >
              <Heart className="h-4 w-4" />
            </button>
            <button
              className="rounded border border-gray-300 bg-white p-2 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
              title="URLコピー"
              type="button"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 基本情報バー */}
      <div className="flex items-center gap-6 rounded-b-lg border border-t-0 border-gray-200 bg-white px-5 py-3 text-sm">
        <div>
          <span className="text-gray-500">現在年収</span>
          <span className="ml-1.5 font-bold text-gray-900">{candidate.currentSalary}万円</span>
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <div>
          <span className="text-gray-500">希望年収</span>
          <span className="ml-1.5 font-bold text-gray-900">{candidate.desiredSalary}万円</span>
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <div className="flex items-center gap-1 text-gray-500">
          <Calendar className="h-3.5 w-3.5" />
          最終ログイン: {candidate.lastLoginDate}
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <div className="text-gray-400 text-xs">ID: {candidate.memberId}</div>
      </div>

      {/* タブ */}
      <div className="mt-4 flex border-gray-200 border-b bg-white">
        {tabs.map((tab) => (
          <button
            className={`relative px-5 py-2.5 text-sm transition-colors ${
              activeTab === tab.id
                ? "bg-white font-medium text-purple-600 after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-purple-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            type="button"
          >
            {tab.label}
            {tab.count !== undefined ? (
              <span className="ml-1.5 text-xs text-gray-400">({tab.count})</span>
            ) : null}
          </button>
        ))}
      </div>

      {/* タブコンテンツ */}
      <div className="mt-4">
        {activeTab === "resume" ? (
          <div className="space-y-4">
            {/* 職務要約 */}
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <SectionHeader>職務要約</SectionHeader>
              <p className="leading-relaxed text-sm text-gray-700">{candidate.summary}</p>
              <div className="mt-2 text-xs text-gray-400">
                文字数: {candidate.resumeCharCount}字
              </div>
            </div>

            {/* 職歴 */}
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <SectionHeader>職歴</SectionHeader>
              <div className="relative space-y-0 pl-4">
                {/* タイムライン線 */}
                <div className="absolute top-1 bottom-1 left-[3px] w-px bg-gray-200" />
                {candidate.workHistories.map((work, i) => (
                  <div className="relative pb-5 last:pb-0" key={work.period}>
                    {/* タイムラインドット */}
                    <div
                      className={`absolute -left-4 top-1.5 h-1.5 w-1.5 rounded-full ${i === 0 ? "bg-purple-500" : "bg-gray-300"}`}
                    />
                    <div className="text-sm font-medium text-gray-900">{work.company}</div>
                    <div className="text-[13px] text-gray-600">
                      {work.department} / {work.position}
                    </div>
                    <div className="text-xs text-gray-400">{work.period}</div>
                    <p className="mt-1.5 text-[13px] text-gray-600">{work.description}</p>
                  </div>
                ))}
              </div>
              {candidate.managementExperience > 0 ? (
                <div className="mt-4 flex items-center gap-1.5 rounded bg-gray-50 px-3 py-2 text-[13px]">
                  <Star className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-gray-700">
                    マネジメント経験: {candidate.managementExperience}名
                  </span>
                </div>
              ) : null}
            </div>

            {/* 学歴 */}
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <SectionHeader>学歴</SectionHeader>
              <div className="flex items-center gap-2 text-sm">
                <GraduationCap className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">
                  {candidate.education.school} {candidate.education.faculty}
                </span>
                <span className="text-gray-500">（{candidate.education.degree}）</span>
                <span className="text-gray-400">{candidate.education.graduationYear}年卒</span>
              </div>
            </div>

            {/* 語学力 */}
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <SectionHeader>語学力</SectionHeader>
              <div className="space-y-1.5">
                {candidate.languages.map((lang) => (
                  <div className="flex items-center gap-2 text-sm" key={lang.language}>
                    <Languages className="h-4 w-4 text-gray-400" />
                    <span className="font-medium text-gray-900">{lang.language}</span>
                    <span className="text-gray-500">— {lang.level}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 希望条件 */}
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <SectionHeader>希望条件</SectionHeader>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-xs text-gray-500">希望職種</span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {candidate.desiredJobCategories.map((cat) => (
                      <span
                        className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-[13px] text-gray-700"
                        key={cat}
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">希望業種</span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {candidate.desiredIndustries.map((ind) => (
                      <span
                        className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-[13px] text-gray-700"
                        key={ind}
                      >
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">希望年収</span>
                  <div className="mt-1 font-medium text-gray-900">
                    {candidate.desiredSalary}万円以上
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">希望勤務地</span>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {candidate.desiredLocations.map((loc) => (
                      <span
                        className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-[13px] text-gray-700"
                        key={loc}
                      >
                        {loc}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === "history" ? (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <p className="text-sm text-gray-500">この候補者との関わり履歴はありません。</p>
          </div>
        ) : null}

        {activeTab === "message" ? (
          <div className="rounded-lg border border-gray-200 bg-white p-5">
            <p className="text-sm text-gray-500">メッセージのやり取りはありません。</p>
            <button
              className="mt-3 rounded bg-purple-600 px-5 py-2 font-medium text-sm text-white hover:bg-purple-700"
              onClick={onScoutClick}
              type="button"
            >
              <Mail className="mr-1.5 inline h-3.5 w-3.5" />
              スカウトを送る
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
